import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskedLines, EASE } from "@/components/Reveal";
import { lenisScroll } from "@/lib/scroll";
import { IMG, HERO_VIDEO_URL } from "@/data/site";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.22]);
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <motion.div style={{ scale, y }} className="absolute inset-0">
        <video
          poster={IMG.hero}
          autoPlay
          muted
          loop
          playsInline
          onCanPlay={(e) => e.currentTarget.play().catch(() => {})}
          className="w-full h-full object-cover"
          aria-label="Cinematic luxury property interior walkthrough — real estate video post-production by VCONTRACTZ"
        >
          <source src="/assets/hero-video.webm" type="video/webm" />
          <source src={HERO_VIDEO_URL} type="video/mp4" />
        </video>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/20 to-ink" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/60 via-transparent to-transparent" />

      <motion.div
        style={{ opacity: fade, y: textY }}
        className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-28 md:pb-32"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="overline-gold mb-6"
          data-testid="hero-overline"
        >
          Real Estate Visual Post-Production &bull; Worldwide
        </motion.p>

        <h1 className="font-display font-light text-[13vw] sm:text-6xl md:text-7xl lg:text-[6.5vw] leading-[0.95] tracking-tight">
          <MaskedLines
            onLoad
            delay={0.45}
            lines={[
              "EVERY PROPERTY",
              "DESERVES A",
              <>
                PERFECT <em className="text-gold not-italic font-display italic">FRAME.</em>
              </>,
            ]}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 1, ease: EASE }}
          className="mt-6 max-w-xl text-sm md:text-base text-ivory/80 font-light leading-relaxed"
          data-testid="hero-subcopy"
        >
          Premium real estate photo, panorama and video post-production for
          photographers, agencies and property brands worldwide.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 1, ease: EASE }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <button data-testid="hero-explore-services-btn" data-cursor="EXPLORE" onClick={() => lenisScroll("#services")} className="btn-primary">
            Explore Our Services
          </button>
          <button data-testid="hero-start-project-btn" data-cursor="START" onClick={() => lenisScroll("#contact")} className="btn-ghost">
            Start a Project
            <ArrowUpRight size={14} />
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => lenisScroll("#trust")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 text-[10px] tracking-[0.35em] text-ivory/60 hover:text-gold transition-colors duration-300"
      >
        SCROLL TO DISCOVER
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.button>
    </section>
  );
}
